'use client';

import React, { useMemo, useRef } from 'react';

interface OtpInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  status?: 'error';
  autoFocus?: boolean;
  id?: string;
  'aria-describedby'?: string;
}

/**
 * One-time-code field split into `length` single-character boxes.
 * - typing a digit advances the caret to the next box
 * - Backspace clears the current box, or steps back when already empty
 * - pasting (or browser/OS autofill of the whole code) spreads across the boxes
 *   — only the first box carries autoComplete="one-time-code" so autofill targets it
 */
export default function OtpInput({
  length = 6,
  value = '',
  onChange,
  disabled,
  status,
  autoFocus,
  id = 'code',
  ...aria
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const chars = useMemo(() => Array.from({ length }, (_, i) => value[i] ?? ''), [value, length]);

  const focusAt = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  const commit = (next: string[], focusIndex?: number) => {
    onChange?.(next.join('').trimEnd());
    if (focusIndex !== undefined) requestAnimationFrame(() => focusAt(focusIndex));
  };

  const fill = (start: number, digits: string[]) => {
    const next = [...chars];
    let i = start;
    for (const d of digits) {
      if (i >= length) break;
      next[i] = d;
      i += 1;
    }
    commit(next, i);
  };

  return (
    <div className={`otp-input${status ? ` ${status}` : ''}`}>
      {chars.map((char, i) => (
        <input
          key={i}
          id={i === 0 ? id : `${id}-${i}`}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className={`ui-input otp-digit${status ? ` ${status}` : ''}`}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus && i === 0}
          // not maxLength=1: autofill and paste need to deliver the full code here
          maxLength={length}
          disabled={disabled}
          value={char}
          aria-label={`${i + 1}/${length}`}
          {...(i === 0 ? aria : {})}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            if (!digits) {
              const next = [...chars];
              next[i] = '';
              commit(next);
              return;
            }
            fill(i, digits.split(''));
          }}
          onPaste={(e) => {
            e.preventDefault();
            const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
            if (digits) fill(i === 0 ? 0 : i, digits.split(''));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              const next = [...chars];
              if (next[i]) {
                next[i] = '';
                commit(next, i);
              } else {
                next[Math.max(0, i - 1)] = '';
                commit(next, i - 1);
              }
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              focusAt(i - 1);
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              focusAt(i + 1);
            }
          }}
        />
      ))}
    </div>
  );
}
